import type { AxisSelection, MatchMode } from '@atlas/types'
import type { ApplicableRule, CreateRuleInput, Rule, RuleElementType } from '@/domain/rules.types'
import { apiClient } from './api.client'

/** Rules Manager API client — real `rule-service` backend. */
export const rulesApi = {
	list: async (): Promise<Rule[]> => {
		const response = await apiClient.get<Rule[]>('/rules')
		// The API returns 204 with no body when there are no rules yet.
		return response.status === 204 || !response.data ? [] : response.data
	},
	byId: (id: string) => apiClient.get<Rule>(`/rules/${id}`).then((r) => r.data),
	create: (input: CreateRuleInput) => apiClient.post<Rule>('/rules', input).then((r) => r.data),
	update: (id: string, patch: Partial<Rule>) => apiClient.patch<Rule>(`/rules/${id}`, patch).then((r) => r.data),
	/**
	 * Coverage resolution: the rules that apply to a context tuple. Each axis becomes
	 * a query key with comma-separated value codes. Always returns an array — unlike
	 * `list`, this endpoint answers 200 with `[]` rather than 204.
	 *
	 * `elementType` narrows the result to rules governing that kind of element; omit it
	 * to get every rule matching the context. `mode` and `elementType` are reserved
	 * query keys, never axis codes.
	 */
	applicable: (context: AxisSelection, mode?: MatchMode, elementType?: RuleElementType): Promise<ApplicableRule[]> => {
		const params: Record<string, string> = {}
		for (const [axisCode, codes] of Object.entries(context)) {
			if (codes && codes.length > 0) params[axisCode] = codes.join(',')
		}
		if (mode) params.mode = mode
		if (elementType) params.elementType = elementType
		return apiClient.get<ApplicableRule[]>('/rules/applicable', { params }).then((r) => r.data ?? [])
	},
}
